import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, effect, inject, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import moment from 'moment';
import { FilterOptions, FilterValues, OpsStreamType } from '../../models/filters.model';
import { DueTagLabel, HandoverLog, HandoverLogPost, HandoverLogPut } from '../../models/handover-log.model';
import { HandoverLogsService } from '../../services/api/handover-logs.api.service';
import { ExportColumns, ExportCSVService } from '../../services/export-csv.service';
import { PaginationStore } from '../../services/stores/pagination.store';
import { SelectionStore } from '../../services/stores/selection.store';
import { UserStore } from '../../services/stores/user.store';
import { TableStateComponent } from '../../shared/components/table-state/table-state.component';
import { GenericDialogComponent, GenericDialogData } from '../dialogs/generic-dialog/generic-dialog.dialog';
import { HandoverFiltersComponent } from './handover-filters/handover-filters.component';
import { HandoverTableComponent } from './handover-table/handover-table.component';
import { HandoverTaskFormValue, TaskDialogData, TaskDialogMode, TaskDialogResult, TaskDialogResultAction } from '../dialogs/task-dialogs/task.dialog.model';
import { HandoverTaskDialog } from '../dialogs/task-dialogs/handover-dialog/handover-task.dialog';
import { ToastService } from '../../services/toastr.service';
import { TagVariant } from '../../shared/components/app-tag/app-tag.component';
import { HandoverLogFilterOptions, HandoverLogFilterValues } from '../../models/handover-filter.model';
import { NO_PROGRAM_ID, NO_PROGRAM_LABEL } from '../../shared/constants/program';

@Component({
  selector: 'dr-ops-stream-ui-handover-logs',
  imports: [HandoverFiltersComponent, HandoverTableComponent, TableStateComponent],
  templateUrl: './handover-logs.component.html',
  styleUrl: './handover-logs.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PaginationStore, SelectionStore],
})
export class HandoverLogsComponent implements OnInit {
  private static readonly DUE_SOON_WINDOW_MS = 5 * 60 * 1000;
  @ViewChild(HandoverFiltersComponent) filtersComponent!: HandoverFiltersComponent;

  private readonly handoverService = inject(HandoverLogsService);
  private dialog = inject(Dialog);
  private readonly exportCSVService = inject(ExportCSVService);
  readonly paginationStore = inject(PaginationStore);
  readonly selectionStore = inject(SelectionStore);
  readonly userStore = inject(UserStore);
  private toast = inject(ToastService);

  private readonly destroyRef = inject(DestroyRef);

  readonly allLogsSignal = signal<HandoverLog[]>([]);
  readonly isLoadingSignal = signal(true);

  readonly activeFiltersSignal = signal<HandoverLogFilterValues>({ activity: null, program: null, createdBy: null, tag: null, dueDate: null, status: OpsStreamType.PENDING });

  readonly filterOptionsSignal = computed<HandoverLogFilterOptions>(() => {
    const logs = this.allLogsSignal();
    const activities = new Set<string>();
    const programs = new Set<string>();
    const creators = new Set<string>();
    const tags = new Set<string>();

    logs.forEach((log) => {
      activities.add(log.activity);
      programs.add(log.programName);
      creators.add(log.createdByName);
      if (log.tagLabel) {
        tags.add(log.tagLabel?.valueOf());
      }
    });

    return {
      activities: [...activities].sort(),
      programs: [...programs].sort(),
      creators: [...creators].sort(),
      tags: [...tags].sort(),
    };
  });

  readonly filteredLogsSignal = computed(() => {
    const filters = this.activeFiltersSignal();
    const logs = this.allLogsSignal();
    const filtered = logs.filter((log) => this.matchesFilters(log, filters));

    if (filters.status === OpsStreamType.RESOLVED) {
      return filtered.sort((a, b) => {
        const da = this.toDate(a.updatedDate)?.getTime() ?? 0;
        const db = this.toDate(b.updatedDate)?.getTime() ?? 0;
        return db - da;
      });
    }

    return filtered;
  });

  readonly selectedOpsStreamTypeSignal = computed<OpsStreamType>(() => {
    const filters = this.activeFiltersSignal();
    return filters.status ?? OpsStreamType.PENDING;
  });

  readonly showCreateButtonSignal = computed(() => {
    const selectedType = this.selectedOpsStreamTypeSignal();
    const selectedCount = this.selectionStore.selectedCountSignal();
    return selectedType === OpsStreamType.PENDING && selectedCount === 0;
  });

  readonly showResolveButtonSignal = computed(() => {
    const selectedType = this.selectedOpsStreamTypeSignal();
    const selectedCount = this.selectionStore.selectedCountSignal();
    return selectedType === OpsStreamType.PENDING && selectedCount > 0;
  });

  readonly showDeleteButtonSignal = computed(() => {
    const selectedCount = this.selectionStore.selectedCountSignal();
    return selectedCount > 0;
  });

  protected readonly exportCSVColumns: ExportColumns<HandoverLog> = Object.freeze([
    { header: 'LOG ID', accessor: (log: HandoverLog) => log.id },
    { header: 'ACTIVITY TYPE', accessor: (log: HandoverLog) => log.activity },
    { header: 'PROGRAM', accessor: (log: HandoverLog) => log.programName },
    { header: 'NOTES', accessor: (log: HandoverLog) => log.notes },
    { header: 'DUE DATE', accessor: (log: HandoverLog) => log.dueDate },
    { header: 'CREATED BY', accessor: (log: HandoverLog) => log.createdByName },
  ]);

  private matchesFilters(log: HandoverLog, filters: HandoverLogFilterValues): boolean {
    // Early return for simple string filters
    if (filters.activity && log.activity !== filters.activity) return false;
    if (filters.program && log.programName !== filters.program) return false;
    if (filters.createdBy && log.createdByName !== filters.createdBy) return false;
    if (filters.tag && log.tagLabel !== filters.tag) return false;
    if (filters.status?.toString() !== log.status) return false;
    // Date comparison with early return
    if (filters.dueDate && !this.isSameDate(log.dueDate, filters.dueDate)) {
      return false;
    }
    return true;
  }

  private isSameDate(date1: string | Date, date2: string | Date): boolean {
    return moment.utc(date1).isSame(moment.utc(date2), 'day');
  }

  readonly pagedLogsSignal = computed(() => this.paginationStore.getPagedData(this.filteredLogsSignal()));
  readonly showTableSignal = computed(() => !this.isLoadingSignal() && this.filteredLogsSignal().length > 0);

  constructor() {
    effect(() => this.selectionStore.setTotalItems(this.filteredLogsSignal().map((log) => log.id)));
  }

  ngOnInit(): void {
    this.loadLogs();
  }

  onFiltersChange(filters: HandoverLogFilterValues): void {
    this.activeFiltersSignal.set({ ...filters, status: this.selectedOpsStreamTypeSignal() });
    this.paginationStore.resetPageIndex(); // return to page 0 when filters change
    this.selectionStore.clearSelection(); // clear selected rows when filters change
  }

  private loadLogs(): void {
    this.isLoadingSignal.set(true);

    this.handoverService
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          const orderedData = this.addTagsAndOrderElements(data);
          this.allLogsSignal.set(orderedData);
          this.isLoadingSignal.set(false);
        },
        error: () => {
          this.isLoadingSignal.set(false);
        },
      });
  }

  addTagsAndOrderElements(data: HandoverLog[]): HandoverLog[] {
    const now = new Date();
    const dueSoonWindowMs = HandoverLogsComponent.DUE_SOON_WINDOW_MS;

    const withTags = data.map((r) => {
      const d = this.toDate(r.dueDate);
      if (!d) return { ...r, tagLabel: undefined, tagVariant: undefined };

      const diffMs = d.getTime() - now.getTime();

      if (diffMs <= 0) {
        return { ...r, tagLabel: DueTagLabel.Overdue, tagVariant: TagVariant.Error };
      }

      if (diffMs <= dueSoonWindowMs) {
        return { ...r, tagLabel: DueTagLabel.DueSoon, tagVariant: TagVariant.Warning };
      }

      return { ...r, tagLabel: undefined, tagVariant: undefined };
    });

    // priority: Overdue -> Due soon -> No tag
    const priority = (r: HandoverLog): number => {
      if (r.tagLabel === DueTagLabel.Overdue) return 0;
      if (r.tagLabel === DueTagLabel.DueSoon) return 1;
      return 2;
    };

    // Sort copy (don’t mutate original array)
    return [...withTags].sort((a, b) => {
      const pa = priority(a);
      const pb = priority(b);
      if (pa !== pb) return pa - pb;

      // Optional: within same priority, sort by due date ascending (earliest first)
      const da = this.toDate(a.dueDate)?.getTime() ?? Number.POSITIVE_INFINITY;
      const db = this.toDate(b.dueDate)?.getTime() ?? Number.POSITIVE_INFINITY;
      return da - db;
    });
  }

  private toDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  protected exportCSV() {
    this.exportCSVService.exportCSV(this.filteredLogsSignal, this.exportCSVColumns);
  }

  protected handleCreate() {
    const ref = this.dialog.open(HandoverTaskDialog, {
      disableClose: true,
      backdropClass: 'app-dialog-backdrop',
      panelClass: 'app-dialog-panel',
      data: {
        title: 'Create Handover Log Activity',
        mode: TaskDialogMode.CREATE,
      } as TaskDialogData,
    }) as DialogRef<TaskDialogResult<HandoverLog, HandoverTaskFormValue>>;

    ref.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (!result || result.action === TaskDialogResultAction.CANCEL) return;

      if (result.action === TaskDialogResultAction.CREATE) this.handleCreateHandoverLogResult(result.resultTaskFormValue as HandoverTaskFormValue);
    });
  }

  handleView(log: HandoverLog) {
    this.dialog.open(HandoverTaskDialog, {
      disableClose: true,
      backdropClass: 'app-dialog-backdrop',
      panelClass: 'app-dialog-panel',
      data: {
        title: 'View Handover Log Activity',
        mode: TaskDialogMode.VIEW,
        defaults: this.logToTaskFormValue(log),
      } satisfies TaskDialogData,
    });
  }

  handleEdit(log: HandoverLog) {
    const ref = this.dialog.open(HandoverTaskDialog, {
      disableClose: true,
      backdropClass: 'app-dialog-backdrop',
      panelClass: 'app-dialog-panel',
      data: {
        title: 'Edit Handover Log Activity',
        mode: TaskDialogMode.EDIT,
        log: log,
        defaults: this.logToTaskFormValue(log),
      } satisfies TaskDialogData,
    }) as DialogRef<TaskDialogResult<HandoverLog, HandoverTaskFormValue>>;

    ref.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (!result || result.action === TaskDialogResultAction.CANCEL) return;

      if (result.action === TaskDialogResultAction.UPDATE && !!result.inputLog)
        this.handleUpdateHandoverLogResult(result.inputLog as HandoverLog, result.resultTaskFormValue as HandoverTaskFormValue);
    });
  }

  private logToTaskFormValue(log: HandoverLog): Partial<HandoverTaskFormValue> {
  return {
    programId: log.programId === NO_PROGRAM_ID ? NO_PROGRAM_LABEL : (log.programId ?? ''),
    activity: log.activity ?? '',
    timezone: log.timezone ?? '',
    notes: log.notes ?? '',
    dueDate: this.isoToDatetimeLocal(log.dueDate),
  };
}

  private isoToDatetimeLocal(value: string | Date | null | undefined): string | undefined {
    if (!value) return undefined;

    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return undefined;

    const pad = (n: number) => String(n).padStart(2, '0');

    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  }

  private handleCreateHandoverLogResult(result: HandoverTaskFormValue) {
    if (!result) return;

    const currentUserId = this.userStore.userIdSignal();
    if (!currentUserId) {
      console.error('User not authenticated, cannot create log');
      return;
    }

    const payload = this.handoverService.createPayload(result, currentUserId);

    this.createHandoverLog(payload);
  }

  private handleUpdateHandoverLogResult(log: HandoverLog, result: HandoverTaskFormValue) {
    if (!result) return;

    const currentUserId = this.userStore.userIdSignal();
    if (!currentUserId) {
      console.error('User not authenticated, cannot create log');
      return;
    }

    const logToUpdate = { ...log, ...result } as HandoverLog;

    const payload = this.handoverService.buildUpdatedPayload(logToUpdate, currentUserId);
    this.updateHandoverLog(log.id, payload);
  }

  private createHandoverLog(payload: HandoverLogPost) {
    this.handoverService
      .create(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.toast.success('Entry created successfully', 'The new entry is now in your list');
        this.loadLogs();
      });
  }

  private updateHandoverLog(id: string, payload: HandoverLogPost) {
    this.handoverService
      .update(id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.toast.success('Entry updated successfully', 'The entry is now updated in your list.');
        this.loadLogs();
      });
  }

  protected resolveLogs() {
    const selectedIds = this.selectionStore.selectedIdsSignal();
    const isMultiple = selectedIds.size > 1;

    const dialogRef = this.dialog.open(GenericDialogComponent, {
      disableClose: true,
      backdropClass: 'app-dialog-backdrop',
      panelClass: 'app-dialog-panel',
      data: {
        title: isMultiple ? 'Resolve Entries' : 'Resolve Entry',
        subTitle: isMultiple ? 'Are you sure you want to resolve these entries?' : 'Are you sure you want to resolve this entry?',
        onConfirmText: 'Confirm',
        onCancelText: 'Cancel',
        onCancel: () => dialogRef.close(false),
        onConfirm: () => dialogRef.close(true),
      } as GenericDialogData,
    });
    dialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed) => this.handleResolveConfirmation(!!confirmed, selectedIds));
  }

  protected handleDeleteLogs() {
    const selectedIds = this.selectionStore.selectedIdsSignal();
    const isMultiple = selectedIds.size > 1;

    const dialogRef = this.dialog.open(GenericDialogComponent, {
      disableClose: true,
      backdropClass: 'app-dialog-backdrop',
      panelClass: 'app-dialog-panel',
      data: {
        title: isMultiple ? 'Delete Handover Logs?' : 'Delete Handover Log?',
        subTitle: isMultiple
          ? `Are you sure you want to delete the ${selectedIds.size} selected Handover Logs? This action is not reversible.`
          : `Are you sure you want to delete the selected Handover Log? This action is not reversible.`,
        onConfirmText: 'Confirm',
        onCancelText: 'Cancel',
        onCancel: () => dialogRef.close(false),
        onConfirm: () => dialogRef.close(true),
      } as GenericDialogData,
    });
    dialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed) => this.handleDeleteConfirmation(!!confirmed, selectedIds));
  }

  private handleDeleteConfirmation(confirmed: boolean | null, selectedIds: Set<string>) {
    if (!confirmed || selectedIds.size === 0) {
      return;
    }
    const userId = this.userStore.userIdSignal();
    if (!userId) {
      console.error('User not authenticated, cannot resolve logs');
      return;
    }

    const isMultiple = selectedIds.size > 1;

    this.handoverService
      .removeMultiple(selectedIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (isMultiple) {
            this.toast.success('Entries deleted successfully', 'The selected entries have been removed from your list');
          } else {
            this.toast.success('Entry deleted successfully', 'The selected entry has been removed from your list');
          }
          this.selectionStore.clearSelection();
          this.loadLogs();
        },
        error: (err) => {
          console.error('Failed to delete selected logs', err);
        },
      });
  }

  private handleResolveConfirmation(confirmed: boolean | null, selectedIds: Set<string>) {
    if (!confirmed || selectedIds.size === 0) {
      return;
    }
    const userId = this.userStore.userIdSignal();
    if (!userId) {
      console.error('User not authenticated, cannot resolve logs');
      return;
    }
    const allLogs = this.filteredLogsSignal();
    const updateModels = this.handoverService.buildResolvedPayloads(allLogs, selectedIds, userId);
    if (updateModels.length === 0) return;
    this.sendResolveRequest(updateModels);
  }

  private sendResolveRequest(models: HandoverLogPut[]) {
    const isMultiple = models.length > 1;
    this.handoverService
      .resolveLogs(models)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (isMultiple) {
            this.toast.success('Entries resolved successfully', 'These entries have been moved to the "Resolved" section');
          } else {
            this.toast.success('Entry resolved successfully', 'This entry has been moved to the "Resolved" section');
          }
          this.selectionStore.clearSelection();
          this.loadLogs();
        },
        error: (err) => console.error('Failed to resolve logs', err),
      });
  }

  public switchItems = [
    { title: 'Pending', data: { content: OpsStreamType.PENDING } },
    { title: 'Resolved', data: { content: OpsStreamType.RESOLVED } },
  ];

  public handleSwitch = (data: { content: OpsStreamType }) => {
    this.selectionStore.clearSelection();
    this.activeFiltersSignal.set({ ...this.activeFiltersSignal(), status: data.content });
    this.paginationStore.resetPageIndex();
    this.filtersComponent.onReset();
  };
}
